import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchProject } from "../api/dashboard.api";

// Prevent duplicate network calls in React 18 dev (StrictMode mounts twice)
// and when multiple components use this hook simultaneously.
const projectFetchCache = new Map();
const PROJECT_PHONE_REFRESH = "wa-project-phone-refresh";

export function invalidateProjectPhoneCache(projectId) {
  const id = String(projectId || "").trim();
  if (id) projectFetchCache.delete(id);
  else projectFetchCache.clear();
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(PROJECT_PHONE_REFRESH, { detail: { projectId: id } }),
    );
  }
}

export function useProjectPhone() {
  const { projectId } = useParams();
  const [phoneNoId, setPhoneNoId] = useState(null);
  const [wabaId, setWabaId] = useState(null);
  const [projectLoading, setProjectLoading] = useState(Boolean(projectId));
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const onRefresh = (event) => {
      const targetId = String(event?.detail?.projectId || "").trim();
      if (!targetId || targetId === String(projectId || "").trim()) {
        setRefreshTick((n) => n + 1);
      }
    };
    window.addEventListener(PROJECT_PHONE_REFRESH, onRefresh);
    return () => window.removeEventListener(PROJECT_PHONE_REFRESH, onRefresh);
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;

    if (!projectId) {
      setPhoneNoId(null);
      setWabaId(null);
      setProjectLoading(false);
      return undefined;
    }

    setPhoneNoId(null);
    setWabaId(null);
    setProjectLoading(true);

    const fetchPromise = (() => {
      if (projectFetchCache.has(projectId)) return projectFetchCache.get(projectId);

      const p = fetchProject(projectId)
        .then((res) => {
          const project = res?.project || res?.data || res;
          const resolvedPhone = project?.phone_number_id
            ? String(project.phone_number_id).trim()
            : null;
          const resolvedWaba = project?.waba_id ? String(project.waba_id).trim() : null;
          return { resolvedPhone: resolvedPhone || null, resolvedWaba: resolvedWaba || null };
        })
        .catch(() => {
          projectFetchCache.delete(projectId);
          return { resolvedPhone: null, resolvedWaba: null };
        });

      projectFetchCache.set(projectId, p);
      return p;
    })();

    fetchPromise
      .then(({ resolvedPhone, resolvedWaba }) => {
        if (cancelled) return;
        setPhoneNoId(resolvedPhone);
        setWabaId(resolvedWaba);
      })
      .finally(() => {
        if (!cancelled) setProjectLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, refreshTick]);

  const whatsappConnected = Boolean(phoneNoId);
  //const needsWhatsAppSetup = Boolean(projectId) && !projectLoading && !phoneNoId;
  const needsWhatsAppSetup = !Boolean(phoneNoId);

  return {
    projectId,
    phoneNoId,
    wabaId,
    projectLoading,
    whatsappConnected,
    needsWhatsAppSetup,
    phoneReady: whatsappConnected && !projectLoading,
  };
}
